import {
  createAppSelector,
  selectCourseUsers,
  selectCurrentCourseId,
  selectCurrentUserId,
} from "./base";
import { selectOrgUserByUserIdMap } from "./org";
import { selectPageById } from "./page";
import type { EnabledCourseUser, UserWithCourseUserType } from "./types";

import type { RootState } from "store";

export const selectEnabledCourseUsers = createAppSelector(
  [selectCourseUsers, selectOrgUserByUserIdMap],
  (courseUsers, orgUsersMap) =>
    courseUsers.filter((cu) => {
      // If the course user is disabled, then of course it's not enabled.
      if (cu.is_disabled) {
        return false;
      }

      // But we're not done; we also need to check if the associated OrgUser is also not disabled.
      // Because the API only responds in the context of the current Org, we can assume
      // that the entire contents of the Redux store are for the current Org.
      // Therefore, we can look up the OrgUser by user_id, without worrying about org_id.
      const orgUser = orgUsersMap[cu.user_id];
      if (!orgUser) {
        return true; // If there's no OrgUser, we assume the user is not disabled.
      }

      return !orgUser.is_disabled;
    }) as EnabledCourseUser[],
);

// TODO: Rather than duplicating the logic above, perhaps we could extract a helper function
// and use in both places.
export const selectDisabledCourseUsers = createAppSelector(
  [selectCourseUsers, selectOrgUserByUserIdMap],
  (courseUsers, orgUsersMap) =>
    courseUsers.filter((cu) => {
      if (cu.is_disabled) {
        return true;
      }

      const orgUser = orgUsersMap[cu.user_id];
      if (!orgUser) {
        return false;
      }

      return orgUser.is_disabled;
    }),
);

export const selectCourseUsersForCurrentUser = createAppSelector(
  [selectCourseUsers, selectCurrentUserId],
  (courseUsers, userId) => courseUsers.filter((cu) => cu.user_id === userId),
);

export const selectAllCourseUsersByUserId = createAppSelector(
  [selectCourseUsers, (state, userId: string | null) => userId],
  (courseUsers, userId) => courseUsers.filter((cu) => cu.user_id === userId),
);

export const selectCourseRoleByUserId = createAppSelector(
  [selectCourseUsers, (state, userId: string | null) => userId, selectCurrentCourseId],
  (courseUsers, userId, courseId) =>
    courseUsers.find((cu) => cu.user_id === userId && cu.course_id === courseId)?.role || null,
);

export const selectCourseUserByUserId = createAppSelector(
  [selectCourseUsers, (state, userId: string | null) => userId, selectCurrentCourseId],
  (courseUsers, userId, courseId) =>
    courseUsers.find((cu) => cu.user_id === userId && cu.course_id === courseId) || null,
);

export const selectCourseUserByCourseId = createAppSelector(
  [selectCourseUsersForCurrentUser, (state, courseId: string | null) => courseId],
  (courseUsers, courseId) => courseUsers.find((cu) => cu.course_id === courseId) || null,
);

export const selectCourseUserIdByUserId = createAppSelector(
  [selectCourseUsers, (state, userId: string | null) => userId, selectCurrentCourseId],
  (courseUsers, userId, courseId) =>
    courseUsers.find((cu) => cu.user_id === userId && cu.course_id === courseId)?.id || null,
);

export const selectCurrentCourseUser = createAppSelector(
  [selectCourseUsers, selectCurrentCourseId, selectCurrentUserId],
  (courseUsers, courseId, userId) =>
    courseUsers.find((cu) => cu.course_id === courseId && cu.user_id === userId),
);

/**
 * Identical to `selectCurrentCourseUser`, but returns `undefined` if the course user is disabled.
 */
export const selectCurrentCourseUserIfEnabled = createAppSelector(
  [selectEnabledCourseUsers, selectCurrentCourseId, selectCurrentUserId],
  (enabledCourseUsers, courseId, userId) =>
    enabledCourseUsers.find((cu) => cu.course_id === courseId && cu.user_id === userId),
);

export const selectCurrentUserIsEnabledForCourse = createAppSelector(
  [selectCurrentCourseUserIfEnabled],
  (enabledCourseUser) => Boolean(enabledCourseUser),
);

export const selectCurrentCourseUserId = createAppSelector(
  [selectCurrentCourseUser],
  (courseUser) => (courseUser ? courseUser.id : null),
);

// In most cases, you should use capabilities (canAuthorCourse, canParticipateInCourse, etc.) instead of roles
// (student, instructor, etc.) to determine what the user can do. For example, instead of checking if the current
// course role is "student", you should check if the user can participate in the course.
export const selectCurrentCourseRole = createAppSelector(
  [selectCurrentCourseUserIfEnabled],
  (courseUser) => courseUser?.role || null,
);

export const selectCourseRoleByCourseId = createAppSelector(
  [selectEnabledCourseUsers, selectCurrentUserId, (state, courseId: string) => courseId],
  (courseUsers, userId, courseId) =>
    courseUsers.find((cu) => cu.course_id === courseId && cu.user_id === userId)?.role || null,
);

export const selectCourseUserById = (state: RootState, courseUserId: string) =>
  state.course_users[courseUserId];

export const selectUserWithCourseUserByCourseUserId = createAppSelector(
  [selectCourseUserById, (state) => state.users],
  (courseUser, users) => {
    if (courseUser) {
      const user = users[courseUser.user_id];
      if (user) {
        return {
          course_user_id: courseUser.id,
          ...courseUser,
          ...user,
        } as UserWithCourseUserType;
      }
    }
  },
);

export const selectEnabledCourseUsersForCurrentCourse = createAppSelector(
  [selectEnabledCourseUsers, selectCurrentCourseId],
  (enabledCourseUsers, courseId) => enabledCourseUsers.filter((cu) => cu.course_id === courseId),
);

export const selectEnabledStudentsForCurrentCourse = createAppSelector(
  [selectEnabledCourseUsersForCurrentCourse],
  (enabledCourseUsers) => enabledCourseUsers.filter((cu) => cu.role === "student"),
);

export const selectEnabledCourseUsersByCourseId = createAppSelector(
  [(state, courseId: string) => courseId, selectEnabledCourseUsers],
  (courseId, enabledCourseUsers) => enabledCourseUsers.filter((cu) => cu.course_id === courseId),
);

export const selectEnabledCourseUsersByPageId = createAppSelector(
  [selectPageById, selectEnabledCourseUsers],
  (page, enabledCourseUsers) => enabledCourseUsers.filter((cu) => cu.course_id === page?.course_id),
);
